/**
 * The moment a badge is earned.
 *
 * Fires on FIRST SIGHT, not on the unlocking event — a checkpoint clears
 * server-side while the member is off the surface (their find gets its fifth
 * like at 2am), so a live push alone would celebrate to an empty room. The
 * block compares what the server says is unlocked against what this browser
 * has already celebrated, which covers both cases with one mechanism.
 */
import React, {useEffect} from "react";
import confetti from "canvas-confetti";
import {Modal} from "@/widgets/Modal";
import {Button} from "@/components/buttons/Button";
import "./BadgeUnlockModal.scss";

/** Modal enter transition; the burst waits it out so it lands on a settled card. */
const MODAL_ENTER_MS = 220;

export interface UnlockedBadge {
    id: string;
    name: string;
    description?: string;
    iconUrl?: string;
}

interface BadgeUnlockModalProps {
    badge: UnlockedBadge;
    /** How many more are waiting behind this one, so the member knows to expect them. */
    remaining?: number;
    onClose: () => void;
}

export const BadgeUnlockModal: React.FC<BadgeUnlockModalProps> = ({badge, remaining, onClose}) => {
    /*
     * Keyed on the badge id, not on mount — a queue of unlocks swaps the badge
     * inside one already-mounted modal, and each one is its own moment.
     */
    useEffect(() => {
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
        /* Let the modal finish its enter transition first, or the burst fires behind it. */
        const t = window.setTimeout(() => {
            [0.25, 0.5, 0.75].forEach(x => confetti({
                particleCount: 150, startVelocity: 35, angle: 90, gravity: 0.2,
                spread: 360, ticks: 800, origin: {x, y: -0.5}, zIndex: 10000,
            }));
        }, MODAL_ENTER_MS);
        return () => window.clearTimeout(t);
    }, [badge.id]);

    return <Modal
        isOpen
        toggle={onClose}
        size="sm"
        contentClassName="badge-unlock"
        ariaLabelledBy="badge-unlock-title"
    >
        <span className="badge-unlock__kicker">Badge unlocked</span>

        <div className="badge-unlock__art">
            {badge.iconUrl
                ? <img src={badge.iconUrl} alt="" />
                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
                </svg>}
        </div>

        <h2 id="badge-unlock-title" className="badge-unlock__name">{badge.name}</h2>
        {badge.description && <p className="badge-unlock__desc">{badge.description}</p>}

        <Button variant="primary" onClick={onClose}>
            {remaining && remaining > 0 ? `Next badge (${remaining} more)` : "Nice"}
        </Button>
    </Modal>;
};

export default BadgeUnlockModal;
